// A text input that filters a dropdown as you type, with keyboard navigation.
//
// Ported from Zanetto Tools (phase 7 stage 2). A drop-in replacement for a
// <select> once the option list is long enough that scrolling it is slower than
// searching it — which for Dayboard is every resource picker on a site with
// thirty machines, and every job picker after a few months of trading.
import { useEffect, useMemo, useRef, useState } from 'react';

export interface Option {
  value: string;
  label: string;
  /** Shown small, to the right — "away: on leave", a job number, a category. */
  note?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  className = 'form-input',
  disabled = false,
  id,
}: {
  options: Option[];
  value: string;
  /** Called with '' to clear. */
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.note ?? '').toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const commit = (option: Option | null) => {
    onChange(option ? option.value : '');
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[highlight];
      if (option) commit(option);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className={className}
        placeholder={placeholder}
        value={open ? query : (selected?.label ?? '')}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />

      {open && (
        <div
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white text-sm shadow-lg"
        >
          {selected && (
            <button
              type="button"
              className="min-h-[36px] w-full border-b border-gray-100 px-3 py-2.5 text-left text-gray-600 hover:bg-gray-50 max-sm:min-h-11"
              // onMouseDown, not onClick: the input's blur would close the list
              // before a click ever landed.
              onMouseDown={(event) => {
                event.preventDefault();
                commit(null);
              }}
            >
              Clear selection
            </button>
          )}

          {filtered.length === 0 && <div className="px-3 py-2.5 text-gray-500">No matches</div>}

          {filtered.map((option, i) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={`flex min-h-[36px] w-full items-center justify-between gap-3 px-3 py-2.5 text-left max-sm:min-h-11 ${
                i === highlight ? 'bg-brand-50 text-brand-800' : 'text-gray-700 hover:bg-gray-50'
              }`}
              onMouseDown={(event) => {
                event.preventDefault();
                commit(option);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span>{option.label}</span>
              {option.note && <span className="flex-none text-xs text-gray-600">{option.note}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
